import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { Size } from 'src/app/interfaces/size';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { UnavailableDialogComponent } from '../unavailable.dialog/unavailable.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { CheckoutService } from '../../services/checkout.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';

@Component({
  selector: 'app-size',
  templateUrl: './size.component.html',
  styleUrls: ['./size.component.scss']
})
export class SizeComponent {
  private scrollService = inject(ScrollService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private dialog = inject(MatDialog);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private sizesService = inject(SizesService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private sizeCards = viewChildren('SizeCard', { read: ElementRef<HTMLDivElement> });
  private container = viewChild('Container', { read: ElementRef });
  private undoRedoChangeSubscription: Subscription | null = null;
  shouldScroll = signal(true);
  // sizes = FINGER_SIZES.map(f => f.toString());
  sizes = computed(() => this.sizesService.values().sort((a,b) => a.value - b.value));
  selectedSize = computed(() => this.selectedAttributeService.getValue().size);
  shouldAnimateSelection = false;
  // availableSizes = computed(async () => (await this.selectedAttributeService.availableAttributes()).sizeIDs);
  // awaitedAvailableSizes = signal<number[]>([]);
  availableSizes = signal<number[]>([]);
  reachableSizes = signal<number[]>([]);
  filteredSizes = signal<Size[]>([]);
  sizesToShow = computed(() => {
    const selectedSize = this.selectedSize();
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableSizes = this.reachableSizes();
    const filteredSizes = this.filteredSizes();
    const sizes = this.sizes().sort((a,b) => a.value - b.value);
    const sizesToUse = filteredSizes.length > 0 ? filteredSizes : sizes;
    if (isShowingUnavailable) {
      const sortedSizes = sizesToUse.sort((a, b) => (reachableSizes.includes(b.id) ? 1 : 0) - (reachableSizes.includes(a.id) ? 1 : 0));
      return sortedSizes;
    } else {
      return sizesToUse.filter(w => reachableSizes.includes(w.id) || w.id === selectedSize?.id);
    }
  });

  constructor() {
    effect(async () => {
      this.loadingService.load('SizeComponent::GetReachable');
      const iframeID = untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null;
      const reachableAttributes = await this.rulesService.getReachableSizes(this.selectedAttributeService.configuredItem(), iframeID);
      this.reachableSizes.set(reachableAttributes);
      this.loadingService.done('SizeComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableSizes.set(availableAttributes.sizeIDs);
    }, { allowSignalWrites: true });

    effect(() => {
      this.navigationService.subtitle.set(this.selectedSize()?.displayName ?? null);
    }, { allowSignalWrites: true });

    effect(async () => {
      const cards = this.sizeCards();
      const a = await this.reachableSizes();
      const container = this.container();
      if (untracked(() => this.shouldScroll()) && container) {
        this.scrollService.scrollToPosition('sizes', container.nativeElement);
        this.shouldScroll.set(a.length !== cards.length);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'size');
    }
  }

  ngAfterViewInit() {
    this.shouldAnimateSelection = true;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.undoRedoChangeSubscription?.unsubscribe();
    const container = this.container();
    if (container) {
      this.scrollService.savePosition('sizes', container.nativeElement.scrollTop);
    }
  }

  async sizeClick(size: Size) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'size', size.shortName);
    }

    if (this.availableSizes().includes(size.id)) {
      if (this.selectedSize() !== size) {
        await this.selectedAttributeService.mutateSize(size);
      }
    } else {
      try {
        this.loadingService.load('SizeComponent::SetClosestSize');
        await this.selectedAttributeService.setClosestBySize(size);
      } catch(e) {
        this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'finger size' });
      } finally {
        this.loadingService.done('SizeComponent::SetClosestSize');
      }
    }
  }

  filterValueExtractor(size: Size) {
    return size.displayName;
  }

  filterChange(filteredSizes: any) {
    this.filteredSizes.set(filteredSizes);
  }
}
